const isProduction = import.meta.env.VITE_MODE === 'production';
const measurementId = import.meta.env.VITE_GA4_MEASUREMENT_ID;

const DENIED_CONSENT = {
	analytics_storage: 'denied',
	ad_storage: 'denied',
	ad_user_data: 'denied',
	ad_personalization: 'denied',
} as const;

/** EU 27, then Iceland/Liechtenstein/Norway, then the UK and Switzerland. */
const EEA_UK_CH = 'AT BE BG HR CY CZ DK EE FI FR DE GR HU IE IT LV LT LU MT NL PL PT RO SK SI ES SE IS LI NO GB CH';

/**
 * Where analytics storage needs prior consent. Google resolves the visitor's region itself from the
 * `region` key, so both defaults ship unconditionally and we never geolocate anyone. Kept identical
 * to the list in qweebi-website so the two properties behave the same for the same visitor.
 */
export const OPT_IN_REGIONS = EEA_UK_CH.split(' ');

export type AnalyticsConsent = 'granted' | 'denied';

declare global {
	interface Window {
		dataLayer?: unknown[];
		gtag?: (...args: unknown[]) => void;
	}
}

const readCookie = (name: string): string | null => {
	const value = document.cookie
		.split(';')
		.map((cookie) => cookie.trim())
		.find((cookie) => cookie.startsWith(`${name}=`))
		?.substring(name.length + 1);
	return value === undefined || value === '' ? null : value;
};

const globalPrivacyControl = (): boolean =>
	(navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl === true;

/**
 * The visitor's explicit choice, or null when they have not made one and the regional default
 * should stand. California, Colorado and Connecticut require honouring Global Privacy Control as an
 * opt-out, so a browser sending it starts denied; an explicit acceptance still wins.
 */
export const analyticsConsent = (): AnalyticsConsent | null => {
	const choice = readCookie('qw_analytics_consent');
	if (choice === 'granted' || choice === 'denied') {
		return choice;
	}
	return globalPrivacyControl() ? 'denied' : null;
};

export const initGoogleAnalytics = (): void => {
	if (!measurementId || window.gtag) {
		return;
	}

	window.dataLayer = window.dataLayer ?? [];
	window.gtag = function () {
		// eslint-disable-next-line prefer-rest-params
		window.dataLayer?.push(arguments);
	};

	// Consent first, before any other command.
	window.gtag('consent', 'default', { ...DENIED_CONSENT, region: OPT_IN_REGIONS, wait_for_update: 500 });
	// Applies to every visitor not matched above.
	window.gtag('consent', 'default', { ...DENIED_CONSENT, analytics_storage: 'granted' });

	const choice = analyticsConsent();
	if (choice !== null) {
		window.gtag('consent', 'update', { ...DENIED_CONSENT, analytics_storage: choice });
	}

	window.gtag('js', new Date());
	window.gtag('config', measurementId, isProduction ? {} : { debug_mode: true });

	const script = document.createElement('script');
	script.async = true;
	script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
	document.head.appendChild(script);
};

export type GaSignUpMethod = 'email_password' | 'google_oauth' | 'microsoft_oauth' | 'clever_oauth' | 'classlink_oauth';

export const trackSignUpEvent = (method: GaSignUpMethod, signUpSource: string | null): void => {
	const cleanedSource = signUpSource?.trim() ?? '';
	window.gtag?.('event', 'sign_up', {
		method: method,
		sign_up_source: cleanedSource === '' ? 'unknown' : cleanedSource,
	});
};

export const trackKycCompletedEvent = (): void => {
	window.gtag?.('event', 'complete_kyc');
};

export type GaEmailVerificationMethod = 'oauth' | 'email_link';

export const trackEmailVerifiedEvent = (method: GaEmailVerificationMethod): void => {
	window.gtag?.('event', 'email_verified', { method: method });
};
